import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, UserPlus, Shield, ShoppingBag, Wallet, MoreVertical, Copy,
  MessageCircle, Lock, Unlock, Trash2, KeyRound, X, Loader2,
} from "lucide-react";

export default function Equipe() {
  const nav = useNavigate();
  const [staff, setStaff] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [credCard, setCredCard] = useState(null); // {username, password, name, telefone}
  const [editingId, setEditingId] = useState(null);
  const [resetFor, setResetFor] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/staff");
      setStaff(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar equipe");
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (s) => {
    await api.put(`/staff/${s.id}`, { active: !s.active });
    toast.success(s.active ? "Funcionário bloqueado" : "Funcionário desbloqueado");
    load();
  };

  const removeStaff = async (s) => {
    if (!window.confirm(`Remover ${s.name}? Clientes atribuídos voltam ao administrador.`)) return;
    try {
      await api.delete(`/staff/${s.id}`);
      toast.success("Funcionário removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const resetPassword = async (s, newPwd) => {
    try {
      const r = await api.post(`/staff/${s.id}/reset-password`, { password: newPwd });
      setCredCard({ name: s.name, username: r.data.username, password: r.data.password_plain, telefone: s.telefone });
      setResetFor(null);
      toast.success("Senha redefinida");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  return (
    <div>
      <button onClick={() => nav("/")} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">Equipe</h1>
          <p className="text-sm text-stone-500 mt-1">Vendedores, cobradores e admins do negócio.</p>
        </div>
        <button
          data-testid="btn-new-staff"
          onClick={() => setShowNew(true)}
          className="btn-primary text-sm py-2 px-3"
        >
          <UserPlus className="w-4 h-4" /> Novo
        </button>
      </div>

      <div className="space-y-3" data-testid="staff-list">
        {staff.map((s) => (
          <StaffCard
            key={s.id}
            s={s}
            isEditing={editingId === s.id}
            onEdit={() => setEditingId(editingId === s.id ? null : s.id)}
            onToggleActive={() => toggleActive(s)}
            onRemove={() => removeStaff(s)}
            onReset={() => setResetFor(s)}
            onSaved={() => { setEditingId(null); load(); }}
          />
        ))}
        {staff.length === 0 && (
          <div className="text-center py-10 text-stone-500 text-sm">Carregando…</div>
        )}
      </div>

      {showNew && (
        <NewStaffModal
          onClose={() => setShowNew(false)}
          onCreated={(created) => {
            setShowNew(false);
            setCredCard({
              name: created.name,
              username: created.username,
              password: created.password_plain,
              telefone: created.telefone,
            });
            load();
          }}
        />
      )}

      {credCard && <CredentialsCard data={credCard} onClose={() => setCredCard(null)} />}

      {resetFor && (
        <ResetPasswordModal
          staff={resetFor}
          onClose={() => setResetFor(null)}
          onConfirm={(pwd) => resetPassword(resetFor, pwd)}
        />
      )}
    </div>
  );
}

function StaffCard({ s, isEditing, onEdit, onToggleActive, onRemove, onReset, onSaved }) {
  const [edit, setEdit] = useState({ name: s.name, telefone: s.telefone || "", roles: { ...(s.roles || {}) } });
  const [saving, setSaving] = useState(false);
  const roles = Object.keys(s.roles || {}).filter((k) => s.roles[k]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/${s.id}`, edit);
      toast.success("Atualizado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };
  return (
    <div className="carne-card p-4" data-testid={`staff-card-${s.username}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)] flex items-center justify-center font-display text-lg font-bold shrink-0">
          {s.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-stone-900 truncate">{s.name}</span>
            {!s.active && <span className="stamp-overdue">Bloqueado</span>}
          </div>
          <div className="text-xs text-stone-500">@{s.username} {s.telefone ? `• ${s.telefone}` : ""}</div>
          <div className="flex gap-1 flex-wrap mt-1">
            {roles.map((r) => (
              <span key={r} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${r === "admin" ? "bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]" : r === "vendedor" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {r}
              </span>
            ))}
            {roles.length === 0 && <span className="text-[10px] text-stone-500">Sem papel</span>}
          </div>
        </div>
        <button data-testid={`btn-staff-menu-${s.username}`} onClick={onEdit} className="p-1 text-stone-400 hover:text-stone-900"><MoreVertical className="w-5 h-5" /></button>
      </div>

      {isEditing && (
        <div className="mt-3 space-y-2 border-t border-stone-100 pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Nome" className="h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            <input value={edit.telefone} onChange={(e) => setEdit({ ...edit, telefone: e.target.value })} placeholder="Telefone" className="h-11 px-3 rounded-lg border border-stone-200 bg-white" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["admin", "vendedor", "cobrador"].map((r) => (
              <label key={r} className="inline-flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={!!edit.roles[r]}
                  onChange={(e) => setEdit({ ...edit, roles: { ...edit.roles, [r]: e.target.checked } })}
                />
                <span className="capitalize">{r}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap pt-1">
            <button data-testid={`btn-save-staff-${s.username}`} onClick={save} disabled={saving} className="btn-primary flex-1 text-sm py-2">
              {saving ? "Salvando..." : "Salvar"}
            </button>
            <button onClick={onReset} className="h-11 px-3 rounded-xl border border-stone-200 text-sm font-bold text-stone-700 inline-flex items-center gap-1"><KeyRound className="w-4 h-4" /> Senha</button>
            <button onClick={onToggleActive} className="h-11 px-3 rounded-xl border border-stone-200 text-sm font-bold text-stone-700 inline-flex items-center gap-1">
              {s.active ? <><Lock className="w-4 h-4" /> Bloquear</> : <><Unlock className="w-4 h-4" /> Desbloquear</>}
            </button>
            {s.username !== "admin" && (
              <button onClick={onRemove} className="h-11 px-3 rounded-xl border border-rose-200 text-sm font-bold text-rose-700 inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Remover</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", username: "", password: "", telefone: "", roles: { vendedor: true, cobrador: false, admin: false } });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9]+$/.test(form.username)) return toast.error("Usuário: só letras minúsculas e números");
    if (form.password.length < 4) return toast.error("Senha com pelo menos 4 caracteres");
    setSaving(true);
    try {
      const r = await api.post("/staff", form);
      toast.success("Funcionário criado");
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-5 w-full max-w-md animate-slide-up" data-testid="new-staff-modal">
        <h3 className="font-display text-lg font-bold mb-3">Novo funcionário</h3>
        <div className="space-y-2">
          <input data-testid="input-new-staff-name" placeholder="Nome*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full h-12 px-3 rounded-xl border border-stone-200 bg-white" />
          <input data-testid="input-new-staff-username" placeholder="Usuário* (ex: joao)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "") })} required className="w-full h-12 px-3 rounded-xl border border-stone-200 bg-white" />
          <input data-testid="input-new-staff-password" type="text" placeholder="Senha inicial*" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full h-12 px-3 rounded-xl border border-stone-200 bg-white" />
          <input data-testid="input-new-staff-telefone" placeholder="Telefone/WhatsApp" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="w-full h-12 px-3 rounded-xl border border-stone-200 bg-white" />
          <div className="flex gap-3 pt-2">
            {[
              { k: "admin", label: "Admin", icon: Shield },
              { k: "vendedor", label: "Vendedor", icon: ShoppingBag },
              { k: "cobrador", label: "Cobrador", icon: Wallet },
            ].map(({ k, label, icon: Icon }) => (
              <label key={k} className="flex-1 cursor-pointer">
                <input type="checkbox" className="peer sr-only" checked={!!form.roles[k]} onChange={(e) => setForm({ ...form, roles: { ...form.roles, [k]: e.target.checked } })} data-testid={`chk-role-${k}`} />
                <div className="rounded-xl border-2 border-stone-200 p-2 text-center peer-checked:border-[color:var(--terracotta)] peer-checked:bg-[color:var(--terracotta-light)] transition-colors">
                  <Icon className="w-5 h-5 mx-auto mb-1 text-stone-700" />
                  <div className="text-xs font-bold text-stone-700">{label}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onClose} className="flex-1 h-12 rounded-xl border border-stone-200 font-bold text-stone-700">Cancelar</button>
          <button data-testid="btn-create-staff" disabled={saving} className="btn-primary flex-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}</button>
        </div>
      </form>
    </div>
  );
}

function CredentialsCard({ data, onClose }) {
  const text = `Olá ${data.name}! Suas credenciais do Carnê Fácil:\nUsuário: ${data.username}\nSenha: ${data.password}\nBaixe o app e entre com esses dados após o login do negócio.`;
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };
  const sendWhats = () => {
    const phone = (data.telefone || "").replace(/\D+/g, "");
    const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-md animate-slide-up" data-testid="credentials-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Credenciais de {data.name}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-2 font-mono-num">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Usuário</div>
            <div data-testid="cred-username" className="text-xl font-bold text-stone-900">{data.username}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Senha</div>
            <div data-testid="cred-password" className="text-xl font-bold text-stone-900">{data.password}</div>
          </div>
        </div>
        <p className="text-xs font-bold text-[color:var(--terracotta)] mt-3 text-center">⚠️ Essa é a única vez que a senha aparece. Guarde!</p>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button data-testid="btn-copy-creds" onClick={copy} className="h-12 rounded-xl border-2 border-stone-300 font-bold text-stone-700 inline-flex items-center justify-center gap-2 hover:bg-stone-50"><Copy className="w-4 h-4" /> Copiar tudo</button>
          <button data-testid="btn-whats-creds" onClick={sendWhats} className="btn-whatsapp justify-center"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ staff, onClose, onConfirm }) {
  const [pwd, setPwd] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-5 w-full max-w-md animate-slide-up">
        <h3 className="font-display text-lg font-bold mb-2">Redefinir senha de {staff.name}</h3>
        <input data-testid="input-reset-password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Nova senha" className="w-full h-12 px-3 rounded-xl border border-stone-200 bg-white" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl border border-stone-200 font-bold text-stone-700">Cancelar</button>
          <button data-testid="btn-confirm-reset" onClick={() => onConfirm(pwd)} disabled={pwd.length < 4} className="btn-primary flex-1">Redefinir</button>
        </div>
      </div>
    </div>
  );
}
